import React, { useEffect, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { AuthContext } from '../../context/AuthContext'
import { MapContext } from '../../context/MapContext'
import { useCity } from '../../context/CityContext'
import { formatAddress } from '../../utils/formatAddress'
import MapLayer from '../MapLayer'
import styles from './OrderStatus.module.css'

const GET_ORDER_STATUS = gql`
    query GetOrderStatus($orderId: ID!) {
        order(id: $orderId) {
            id
            status
            price
            paid
            points {
                sort
                address
                latitude
                longitude
                cityId
                cityName
                streetHouse
                countryName
            }
            driverProfile {
                id
                firstName
                lastName
                middleName
                phoneNumber
                carPlateNumber
                carColor
                carDescription
                driverType {
                    id
                    title
                }
            }
        }
    }
`

interface OrderRoutePoint {
    latitude: number
    longitude: number
}

interface DriverLocation {
    latitude: number
    longitude: number
}

interface StatusOrderPoint {
    sort: number
    address?: string | null
    latitude?: number | null
    longitude?: number | null
    cityId?: number | null
    cityName?: string | null
    streetHouse?: string | null
    countryName?: string | null
}

interface StatusOrder {
    id: string
    status: string
    price: number
    paid: boolean
    points: StatusOrderPoint[]
    driverProfile?: {
        id: number
        firstName: string
        lastName: string
        middleName?: string | null
        phoneNumber: string
        carPlateNumber?: string | null
        carColor?: string | null
        carDescription?: string | null
        driverType?: { id: number; title: string } | null
    } | null
}

const STATUS_LABELS: Record<string, string> = {
    '1': 'Поиск водителя',
    '2': 'Водитель найден',
    '3': 'Поиск нового водителя',
    '7': 'Водитель в пути',
    '8': 'Водитель ожидает',
    '5': 'Поездка',
    '6': 'Поездка завершена',
    '4': 'Отменён'
}

function formatEta(seconds: number): string {
    if (!seconds || seconds <= 0) return ''
    const minutes = Math.max(1, Math.round(seconds / 60))
    return `≈ ${minutes} мин`
}

function routeToFeatureCollection(
    route: OrderRoutePoint[]
): GeoJSON.FeatureCollection | null {
    if (route.length < 2) return null
    return {
        type: 'FeatureCollection',
        features: [
            {
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: route.map((p) => [p.longitude, p.latitude])
                },
                properties: {}
            }
        ]
    }
}

export const OrderStatus = ({
    orderId,
    onDone
}: {
    orderId: string
    onDone: () => void
}) => {
    const { state } = React.useContext(AuthContext)
    const authUri = process.env.REACT_APP_AUTH_URI
    const { setRoute, setDriverLocation, updateOrderPoints } =
        React.useContext(MapContext)
    const { selectedCity } = useCity()
    const selected = {
        cityId: selectedCity?.id ?? null,
        countryName: selectedCity?.countryName ?? null
    }

    const [route, setRouteState] = useState<OrderRoutePoint[]>([])
    const [driverLocation, setDriverLocationState] =
        useState<DriverLocation | null>(null)
    const [etaSeconds, setEtaSeconds] = useState(0)

    const { data, loading } = useQuery<{ order: StatusOrder | null }, { orderId: string }>(
        GET_ORDER_STATUS,
        {
            variables: { orderId },
            pollInterval: 5000
        }
    )

    // Поллинг REST-маршрута (гео водителя + полилиния + ETA).
    useEffect(() => {
        if (!orderId || !state.userToken || !authUri) return
        const token = state.userToken
        let active = true
        const load = async () => {
            try {
                const res = await fetch(`${authUri}/order/${orderId}/route`, {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                if (!res.ok) return
                const json = await res.json()
                if (!active) return
                setRouteState(Array.isArray(json.route) ? json.route : [])
                setDriverLocationState(json.driverLocation ?? null)
                setEtaSeconds(
                    typeof json.etaSeconds === 'number' ? json.etaSeconds : 0
                )
            } catch {
                // Транзиентная ошибка сети — оставляем последние данные.
            }
        }
        load()
        const id = setInterval(load, 5000)
        return () => {
            active = false
            clearInterval(id)
        }
    }, [orderId, state.userToken, authUri])

    // Прогоняем маршрут/гео через MapContext, чтобы их отрисовал MapLayer.
    useEffect(() => {
        setRoute(routeToFeatureCollection(route))
        setDriverLocation(driverLocation)
    }, [route, driverLocation, setRoute, setDriverLocation])

    // Точки заказа (подача/назначение) — как маркеры на карте.
    useEffect(() => {
        const order = data?.order
        if (!order) return
        const points = order.points
            .filter(
                (p) =>
                    typeof p.latitude === 'number' &&
                    typeof p.longitude === 'number'
            )
            .map((p) => ({
                ...p,
                latitude: p.latitude as number,
                longitude: p.longitude as number
            }))
        updateOrderPoints(points as any)
    }, [data])

    if (loading && !data?.order) {
        return <div className={styles.loading}>Загрузка…</div>
    }

    if (!data?.order) {
        return (
            <div className={styles.container}>
                <MapLayer interactive={false} />
                <aside className={styles.panel}>
                    <h2 className={styles.status}>Заказ не найден</h2>
                    <button className={styles.button} onClick={onDone}>
                        Вернуться
                    </button>
                </aside>
            </div>
        )
    }

    const order = data.order
    const driver = order.driverProfile
    const from = formatAddress(order.points[0] as any, selected) || '—'
    const to =
        formatAddress(
            order.points[order.points.length - 1] as any,
            selected
        ) || '—'

    return (
        <div className={styles.container}>
            <MapLayer interactive={false} />
            {etaSeconds > 0 && (
                <div className={styles.eta}>
                    Приедет {formatEta(etaSeconds)}
                </div>
            )}
            <aside className={styles.panel}>
                <h2 className={styles.status}>
                    {STATUS_LABELS[order.status] ?? order.status}
                </h2>
                <div className={styles.price}>
                    {order.price} ₽{order.paid ? '' : ' · не оплачен'}
                </div>

                {driver ? (
                    <div className={styles.driver}>
                        <div className={styles.avatar}>
                            {(driver.firstName || 'В')
                                .slice(0, 1)
                                .toUpperCase()}
                        </div>
                        <div>
                            <div className={styles.driverName}>
                                {driver.firstName} {driver.lastName}
                            </div>
                            <div className={styles.driverMeta}>
                                {[
                                    driver.driverType?.title,
                                    driver.carDescription,
                                    driver.carPlateNumber
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </div>
                            {driver.phoneNumber && (
                                <a
                                    className={styles.phone}
                                    href={`tel:${driver.phoneNumber}`}
                                >
                                    {driver.phoneNumber}
                                </a>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={styles.driverName}>
                        Водитель ещё не назначен
                    </div>
                )}

                <div className={styles.route}>
                    <div className={styles.routeLabel}>Откуда</div>
                    <div className={styles.routeValue}>{from}</div>
                    <div className={styles.routeLabel}>Куда</div>
                    <div className={styles.routeValue}>{to}</div>
                </div>

                <button className={styles.button} onClick={onDone}>
                    Завершить
                </button>
            </aside>
        </div>
    )
}
